/* Copyright (c) 2026 PearlCrm Pty Ltd. All rights reserved. */

/**
 * Phone menus. "Press 1 for sales."
 *
 * Two things on this page exist because of how a misconfigured menu fails.
 *
 * The preview shows exactly what the caller hears, from the stored text. The
 * prompt is never composed at call time -- an admin who cannot see the words
 * being read out has no way to notice that "Sales queue" comes out as "Sales
 * queue two" -- so the Suggest button fills the box and leaves it editable.
 *
 * Validate is a button rather than a passive badge. A digit pointing at an
 * empty queue is not an error the save can refuse: it works, it just leaves the
 * caller on hold until the queue times out. That is worth saying out loud and
 * not worth blocking on, and nothing else in the product would ever mention it.
 */

import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  IconButton,
  MenuItem,
  Snackbar,
  Switch,
  TextField,
  Tooltip,
} from "@mui/material";
import {
  Add,
  ArrowBack,
  CheckCircle,
  Delete,
  ErrorOutline,
  WarningAmber,
} from "@mui/icons-material";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useAuth } from "auth/Auth";
import { getApiErrorMessage } from "utils/apiErrors";
import { useTheme } from "@mui/material/styles";
import PageHeader from "components/v2/PageHeader";

const CARD_BORDER = "1px solid #E6E8F0";

const TARGETS = [
  { value: "queue", label: "A queue", needs: "queue" },
  { value: "menu", label: "Another menu", needs: "menu" },
  { value: "user", label: "A specific person", needs: "user" },
  { value: "owner", label: "Whoever owns this customer", needs: null },
  { value: "voicemail", label: "Voicemail", needs: null },
  { value: "message", label: "Play a message and hang up", needs: "message" },
  { value: "hangup", label: "Hang up", needs: null },
  // SEEP AI add-on. If the assistant cannot take the call (not purchased,
  // out of minutes), the caller gets this menu's fallback instead.
  { value: "seep_ai", label: "SEEP AI travel assistant", needs: null },
];

function OptionDialog({ open, onClose, onSave, queues, menus, users, menuId, saving }) {
  const [digits, setDigits] = useState("");
  const [label, setLabel] = useState("");
  const [targetType, setTargetType] = useState("queue");
  const [targetId, setTargetId] = useState("");
  const [message, setMessage] = useState("");
  const [priority, setPriority] = useState("");

  useEffect(() => {
    if (open) {
      setDigits("");
      setLabel("");
      setTargetType("queue");
      setTargetId("");
      setMessage("");
      setPriority("");
    }
  }, [open]);

  const needs = TARGETS.find((t) => t.value === targetType)?.needs;
  const valid =
    digits &&
    label.trim() &&
    (needs === null || (needs === "message" ? message.trim() : targetId));

  const build = () => {
    const payload = {
      digits,
      label: label.trim(),
      target_type: targetType,
      queue_priority: priority === "" ? null : Number(priority),
    };
    if (needs === "queue") payload.target_queue_id = Number(targetId);
    if (needs === "menu") payload.target_menu_id = Number(targetId);
    if (needs === "user") payload.target_user_id = Number(targetId);
    if (needs === "message") payload.message = message.trim();
    return payload;
  };

  const choices =
    needs === "queue" ? queues : needs === "menu" ? menus.filter((m) => m.id !== menuId) : users;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Add a key</DialogTitle>
      <DialogContent>
        <TextField
          fullWidth
          size="small"
          label="Caller presses"
          value={digits}
          onChange={(e) => setDigits(e.target.value.replace(/[^0-9*#]/g, ""))}
          margin="normal"
          autoFocus
          helperText="Digits, * or #."
        />
        <TextField
          fullWidth
          size="small"
          label="What it is for"
          placeholder="Sales"
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          margin="normal"
          helperText="Used in reports, and in the suggested prompt."
        />
        <TextField
          select
          fullWidth
          size="small"
          label="Send the caller to"
          value={targetType}
          onChange={(e) => {
            setTargetType(e.target.value);
            setTargetId("");
          }}
          margin="normal"
        >
          {TARGETS.map((t) => (
            <MenuItem key={t.value} value={t.value}>
              {t.label}
            </MenuItem>
          ))}
        </TextField>

        {needs === "message" ? (
          <TextField
            fullWidth
            size="small"
            multiline
            rows={2}
            label="Message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            margin="normal"
          />
        ) : needs ? (
          <TextField
            select
            fullWidth
            size="small"
            label={needs === "queue" ? "Queue" : needs === "menu" ? "Menu" : "Person"}
            value={targetId}
            onChange={(e) => setTargetId(e.target.value)}
            margin="normal"
          >
            {choices.map((c) => (
              <MenuItem key={c.id} value={c.id}>
                {c.name ||
                  [c.first_name, c.last_name].filter(Boolean).join(" ") ||
                  c.username}
              </MenuItem>
            ))}
          </TextField>
        ) : null}

        {needs === "queue" ? (
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Jump the queue (1 = first)"
            value={priority}
            onChange={(e) => setPriority(e.target.value)}
            margin="normal"
            helperText="Blank uses the queue's own priority. Use for an emergency option."
          />
        ) : null}
      </DialogContent>
      <DialogActions>
        <MDButton variant="text" color="dark" onClick={onClose}>
          Cancel
        </MDButton>
        <MDButton
          variant="gradient"
          color="info"
          disabled={!valid || saving}
          onClick={() => onSave(build())}
        >
          Add
        </MDButton>
      </DialogActions>
    </Dialog>
  );
}

function MenuCard({ menu, axiosInstance, queues, menus, users, onChanged, setError, setToast }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [optionOpen, setOptionOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [findings, setFindings] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axiosInstance.get(`/call-center/ivr-menus/${menu.id}`);
      setDetail(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not load this menu."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance, menu.id, setError]);

  useEffect(() => {
    load();
  }, [load]);

  const patch = async (body, message) => {
    setError("");
    try {
      const res = await axiosInstance.patch(`/call-center/ivr-menus/${menu.id}`, body);
      setDetail(res.data);
      setToast(message);
      onChanged();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not save that."));
      load();
    }
  };

  const addOption = async (payload) => {
    setSaving(true);
    setError("");
    try {
      await axiosInstance.post(`/call-center/ivr-menus/${menu.id}/options`, payload);
      setOptionOpen(false);
      await load();
      setToast("Key added.");
    } catch (err) {
      // 400 here is a loop the server refused, and its message names the path.
      setError(getApiErrorMessage(err, "Could not add that key."));
    } finally {
      setSaving(false);
    }
  };

  const removeOption = async (option) => {
    setError("");
    try {
      await axiosInstance.delete(`/call-center/ivr-menus/${menu.id}/options/${option.id}`);
      await load();
      setToast("Key removed.");
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not remove that key."));
    }
  };

  const suggest = async () => {
    try {
      const res = await axiosInstance.get(
        `/call-center/ivr-menus/${menu.id}/suggested-prompt`
      );
      // Fills the field and leaves it editable rather than saving: the admin
      // has to be able to see and change the words the caller hears.
      setDetail((d) => ({ ...d, greeting_text: res.data.prompt }));
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not suggest a prompt."));
    }
  };

  const validate = async () => {
    setError("");
    try {
      const res = await axiosInstance.post(`/call-center/ivr-menus/${menu.id}/validate`);
      setFindings(res.data);
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not check this menu."));
    }
  };

  if (loading || !detail) {
    return (
      <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px", mb: 3 }}>
        <MDBox display="flex" justifyContent="center" py={2}>
          <CircularProgress size={20} />
        </MDBox>
      </Card>
    );
  }

  return (
    <Card sx={{ p: 3, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px", mb: 3 }}>
      <MDBox display="flex" alignItems="flex-start" gap={1} mb={1}>
        <MDBox flexGrow={1}>
          <MDTypography variant="h6" fontWeight="medium">
            {detail.name}
          </MDTypography>
          <MDTypography variant="caption" color="text">
            {detail.ivr_key} · v{detail.version} ·{" "}
            {detail.used_by_numbers.length
              ? `answers ${detail.used_by_numbers.join(", ")}`
              : "not answering any number yet"}
          </MDTypography>
        </MDBox>
        <Tooltip
          title={
            detail.is_active
              ? "Live. Callers reaching this menu hear it."
              : "Off. Numbers pointing here fall through to their queue."
          }
        >
          <Switch
            checked={detail.is_active}
            onChange={(e) =>
              patch(
                { is_active: e.target.checked },
                e.target.checked ? "Menu is live." : "Menu switched off."
              )
            }
          />
        </Tooltip>
      </MDBox>

      {!detail.is_active ? (
        <Alert severity="info" sx={{ mb: 2 }}>
          This menu is off. Point a phone number at it and switch it on to use it.
        </Alert>
      ) : null}

      <Divider sx={{ my: 2 }} />

      <TextField
        fullWidth
        size="small"
        multiline
        rows={2}
        label="What the caller hears"
        value={detail.greeting_text || ""}
        onChange={(e) => setDetail((d) => ({ ...d, greeting_text: e.target.value }))}
        onBlur={(e) =>
          e.target.value !== menu.greeting_text &&
          patch({ greeting_text: e.target.value }, "Greeting saved.")
        }
        helperText="Read out exactly as written."
      />
      <MDBox display="flex" gap={1} mt={1} mb={2}>
        <MDButton variant="text" color="info" size="small" onClick={suggest}>
          Suggest from the keys below
        </MDButton>
        <MDButton variant="text" color="dark" size="small" onClick={validate}>
          Check this menu
        </MDButton>
      </MDBox>

      {findings ? (
        <MDBox mb={2}>
          {findings.findings.length === 0 ? (
            <Alert severity="success" icon={<CheckCircle />}>
              Nothing wrong with this menu.
            </Alert>
          ) : (
            findings.findings.map((f, i) => (
              <Alert
                key={i}
                severity={f.severity === "error" ? "error" : "warning"}
                icon={f.severity === "error" ? <ErrorOutline /> : <WarningAmber />}
                sx={{ mb: 1 }}
              >
                {f.message}
              </Alert>
            ))
          )}
        </MDBox>
      ) : null}

      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
        <MDTypography variant="button" fontWeight="medium" color="text" flexGrow={1}>
          Keys
        </MDTypography>
        <MDButton
          variant="outlined"
          color="info"
          size="small"
          startIcon={<Add />}
          onClick={() => setOptionOpen(true)}
        >
          Add key
        </MDButton>
      </MDBox>

      {detail.options.length === 0 ? (
        <Alert severity="warning" sx={{ mb: 2 }}>
          No keys, so every caller falls straight through to the fallback.
        </Alert>
      ) : (
        detail.options.map((o) => (
          <MDBox
            key={o.id}
            display="flex"
            alignItems="center"
            gap={1.5}
            sx={{ borderBottom: "1px solid #F2F4F8", py: 1 }}
          >
            <Chip size="small" label={o.digits} />
            <MDBox flexGrow={1}>
              <MDTypography variant="button" fontWeight="medium">
                {o.label}
              </MDTypography>
              <MDTypography variant="caption" color="text" display="block">
                → {o.target_label || o.target_type}
                {o.queue_priority ? ` · priority ${o.queue_priority}` : ""}
                {" · key "}
                {o.option_key}
              </MDTypography>
            </MDBox>
            <IconButton size="small" onClick={() => removeOption(o)}>
              <Delete fontSize="small" />
            </IconButton>
          </MDBox>
        ))
      )}

      <Divider sx={{ my: 2 }} />

      <Grid container spacing={2}>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Wait for a key (s)"
            defaultValue={detail.timeout_seconds}
            onBlur={(e) =>
              patch({ timeout_seconds: Number(e.target.value) }, "Saved.")
            }
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            type="number"
            label="Retries"
            defaultValue={detail.max_retries}
            onBlur={(e) => patch({ max_retries: Number(e.target.value) }, "Saved.")}
            helperText="Then the fallback."
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            fullWidth
            size="small"
            label="Repeat key"
            defaultValue={detail.repeat_digit || ""}
            onBlur={(e) => patch({ repeat_digit: e.target.value || null }, "Saved.")}
            helperText="Does not count as a retry."
          />
        </Grid>
        <Grid item xs={6} md={3}>
          <TextField
            select
            fullWidth
            size="small"
            label="If nobody chooses"
            value={detail.fallback_type}
            onChange={(e) => patch({ fallback_type: e.target.value }, "Fallback saved.")}
          >
            <MenuItem value="queue">A queue</MenuItem>
            <MenuItem value="voicemail">Voicemail</MenuItem>
            <MenuItem value="hangup">Hang up</MenuItem>
          </TextField>
        </Grid>
        {detail.fallback_type === "queue" ? (
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              size="small"
              label="Fallback queue"
              value={detail.fallback_queue_id || ""}
              onChange={(e) =>
                patch({ fallback_queue_id: Number(e.target.value) }, "Fallback saved.")
              }
            >
              {queues.map((q) => (
                <MenuItem key={q.id} value={q.id}>
                  {q.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        ) : null}
      </Grid>

      <OptionDialog
        open={optionOpen}
        onClose={() => setOptionOpen(false)}
        onSave={addOption}
        queues={queues}
        menus={menus}
        users={users}
        menuId={menu.id}
        saving={saving}
      />
    </Card>
  );
}

function TelephonyIvrPage() {
  const isV2 = useTheme()?.pearlUi === "v2";
  const { axiosInstance } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [menus, setMenus] = useState([]);
  const [queues, setQueues] = useState([]);
  const [users, setUsers] = useState([]);
  const [newName, setNewName] = useState("");
  const [creating, setCreating] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const res = await axiosInstance.get("/call-center/ivr-menus", {
        params: { include_inactive: true },
      });
      setMenus(Array.isArray(res.data) ? res.data : []);
      const [q, u] = await Promise.allSettled([
        axiosInstance.get("/call-center/queues", { params: { with_stats: false } }),
        axiosInstance.get("/users/"),
      ]);
      setQueues(q.status === "fulfilled" && Array.isArray(q.value.data) ? q.value.data : []);
      setUsers(
        u.status === "fulfilled" && Array.isArray(u.value.data)
          ? u.value.data.filter((x) => x.is_active && !x.is_support_user)
          : []
      );
    } catch (err) {
      setError(getApiErrorMessage(err, "Unable to load phone menus."));
    } finally {
      setLoading(false);
    }
  }, [axiosInstance]);

  useEffect(() => {
    load();
  }, [load]);

  const create = async () => {
    setCreating(true);
    setError("");
    try {
      // Created switched off: a menu with no keys would answer every caller
      // with silence and then its fallback.
      await axiosInstance.post("/call-center/ivr-menus", {
        name: newName.trim(),
        is_active: false,
      });
      setNewName("");
      setToast("Menu created, switched off.");
      await load();
    } catch (err) {
      setError(getApiErrorMessage(err, "Could not create that menu."));
    } finally {
      setCreating(false);
    }
  };

  // The header's actions, shared by the v2 page header and the classic one.
  const headerActions = (
    <>
      <TextField
        size="small"
        placeholder="New menu name"
        value={newName}
        onChange={(e) => setNewName(e.target.value)}
        sx={{ width: 200 }}
      />
      <MDButton
        variant="gradient"
        color="info"
        size="small"
        startIcon={<Add />}
        disabled={!newName.trim() || creating}
        onClick={create}
      >
        Create
      </MDButton>
    </>
  );

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={isV2 ? 0 : 3}>
        {isV2 ? (
          <PageHeader
            title="Phone menus"
            summary="What a caller hears before they reach anyone, and which key sends them where."
            actions={headerActions}
          />
        ) : (
          <MDBox display="flex" alignItems="center" gap={1} mb={2}>
            <IconButton onClick={() => navigate("/settings/telephony")}>
              <ArrowBack />
            </IconButton>
            <MDBox flexGrow={1}>
              <MDTypography variant="h5" fontWeight="medium">
                Phone menus
              </MDTypography>
              <MDTypography variant="body2" color="text">
                What a caller hears before they reach anyone, and which key sends them where.
              </MDTypography>
            </MDBox>
            {headerActions}
          </MDBox>
        )}

        {error ? (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        ) : null}

        {loading ? (
          <MDBox display="flex" justifyContent="center" py={6}>
            <CircularProgress size={26} />
          </MDBox>
        ) : menus.length === 0 ? (
          <Card sx={{ p: 4, border: CARD_BORDER, boxShadow: "none", borderRadius: "12px" }}>
            <MDTypography variant="body2" color="text">
              No phone menus yet. Create one, add its keys, then point a number at it in Phone
              numbers.
            </MDTypography>
          </Card>
        ) : (
          menus.map((m) => (
            <MenuCard
              key={m.id}
              menu={m}
              axiosInstance={axiosInstance}
              queues={queues}
              menus={menus}
              users={users}
              onChanged={load}
              setError={setError}
              setToast={setToast}
            />
          ))
        )}
      </MDBox>

      <Snackbar
        open={Boolean(toast)}
        autoHideDuration={3000}
        onClose={() => setToast("")}
        message={toast}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </DashboardLayout>
  );
}

export default TelephonyIvrPage;
